import UnderlineLink from "@modules/common/components/underline-link"
import { Transition } from "@headlessui/react"
import { FC, ReactNode, useEffect, useRef } from "react"
import { useIntersection } from "react-use"
import Image, { StaticImageData } from "next/image"
import clsx from "clsx"
import { AccordionProp } from "@lib/data/_types"
import { ImageLoader } from "@lib/util/loader"

type ContentContainerProps = {
    background?: "white" | "dark" | "yellow" | "nograin"
    image: string | StaticImageData
    imageAlt: string
    caption?: string
    title?: string | ReactNode
    titleTag: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p"
    children?: ReactNode
    cta?: {
        text: string | ReactNode
        url: string
        position?:
            | "under title"
            | "under text left"
            | "under text right"
            | "under text 100%"
    }
    side?: "left" | "right"
    imageLocation?: "left 1/3" | "left 3/5" | "right 1/3" | "right 3/5"
    preloadImage?: boolean
    titleVertical?: "top" | "middle" | "bottom"
    content?: string
    accordion?: Array<AccordionProp>
}

const ContentContainer: FC<ContentContainerProps> = (props) => {
    const intersectionRef = useRef(null)
    const intersection = useIntersection(intersectionRef, {
        root: null,
        rootMargin: "0px",
        threshold: 0.5,
    })
    const parts = props?.imageLocation?.split(" ")[1] === "1/3" ? 33 : 66
    const side =
        props?.imageLocation === "right 1/3" ||
        props?.imageLocation === "right 3/5"
            ? "right"
            : "left"
    return (
        <div className={clsx("relative transition w-full py-10 lg:py-[5vh]")}>
            <div
                className={clsx("relative z-10 flex lg:gap-28 gap-10", {
                    "flex-col-reverse md:flex-row-reverse": side === "right",
                    "flex-col-reverse md:flex-row": side === "left",
                })}
            >
                {props.image && (
                    <div
                        className={clsx(
                            "relative w-full aspect-[5/3] overflow-hidden",
                            {
                                "lg:w-[40%]": parts === 33,
                                "lg:w-[60%]": parts === 66,
                            }
                        )}
                        ref={intersectionRef}
                    >
                        <Transition
                            as="div"
                            show={
                                intersection &&
                                intersection.intersectionRatio >= 0.5
                                    ? true
                                    : false
                            }
                            className="relative inset-0 w-full h-full"
                            enter="transform transition duration-[400ms]"
                            enterFrom={`opacity-0 ${
                                side === "right"
                                    ? "translate-x-40"
                                    : "-translate-x-40"
                            }`}
                            enterTo="opacity-100 translate-x-0"
                            leave="transform duration-200 delay-1000 transition ease-in-out"
                            leaveFrom="opacity-100 rotate-0 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            {props?.caption && (
                                <span
                                    className={
                                        "absolute z-10 text-white ml-10 mt-10 font-semibold text-lg"
                                    }
                                    dangerouslySetInnerHTML={{
                                        __html:
                                            props?.caption?.replaceAll(
                                                "\n",
                                                "<br/>"
                                            ) ?? "",
                                    }}
                                ></span>
                            )}
                            <Image
                                loader={ImageLoader}
                                src={props.image}
                                loading="lazy"
                                priority={props?.preloadImage}
                                layout="fill"
                                quality={90}
                                alt={props.imageAlt}
                                className="object-cover hover:shadow-outline w-full max-w-[70vw]"
                                placeholder={
                                    typeof props.image === "string"
                                        ? "empty"
                                        : "blur"
                                }
                            />
                        </Transition>
                    </div>
                )}
                {props?.title !== "" ? (
                    <div
                        className={clsx(
                            "py-4 small:py-16 font-mono w-full flex flex-col justify-center text-darkTone",
                            {
                                "px-4 small:px-8 md:pl-12 md:pr-0":
                                    side === "right",
                                "px-4 small:px-8 md:pl-0 md:pr-12":
                                    side !== "right",
                                "lg:w-[40%]": parts === 66,
                                "lg:w-[60%]": parts === 33,
                            }
                        )}
                    >
                        {props.title && (
                            <props.titleTag className="text-2xl md:text-3xl lg:text-4xl 2xl:text-5xl font-display font-base subpixel-antialiased mb-6">
                                {props.title}
                            </props.titleTag>
                        )}
                        {props.cta &&
                            (typeof props.cta?.position === "undefined" ||
                                props.cta?.position === "under title") && (
                                <UnderlineLink href={props.cta.url}>
                                    {props.cta.text}
                                </UnderlineLink>
                            )}
                        {typeof props?.content === "undefined" ? (
                            <div
                                className={clsx(
                                    "text-base-regular mb-6  mr-auto w-full",
                                    {
                                        "max-w-[32rem]": side !== "right",
                                    },
                                    {
                                        "max-w-[40rem]": side === "right",
                                    }
                                )}
                            >
                                {props.children}
                            </div>
                        ) : (
                            <div
                                className={clsx(
                                    "text-base-regular mb-6  mr-auto w-full",
                                    {
                                        "max-w-[32rem]": side !== "right",
                                    },
                                    {
                                        "max-w-[40rem]": side === "right",
                                    }
                                )}
                                dangerouslySetInnerHTML={{
                                    __html: props?.content ?? "",
                                }}
                            ></div>
                        )}
                        {props.cta &&
                            props.cta?.position !== "under title" &&
                            typeof props.cta?.position !== "undefined" && (
                                <UnderlineLink href={props.cta.url}>
                                    {props.cta.text}
                                </UnderlineLink>
                            )}
                    </div>
                ) : (
                    <div
                        className={clsx(
                            "py-16 font-mono w-full lg:w-[40%] flex flex-col justify-center",
                            {
                                "px-8 md:pl-12 md:pr-0": side === "right",
                            },
                            {
                                "px-8 md:pl-0 md:pr-12": side !== "right",
                            },
                            {
                                "text-darkTone":
                                    (props?.background ?? "dark") === "dark",
                            },
                            { "text-darkTone": props?.background !== "dark" }
                        )}
                        dangerouslySetInnerHTML={{
                            __html: props.content ?? "",
                        }}
                    ></div>
                )}
            </div>

            <div
                className={"absolute inset-0 z-0 pointer-events-none"}
                style={{
                    background:
                        "linear-gradient(\n" +
                        "    to right,\n" +
                        "    transparent 0%,\n" +
                        "    transparent 33.33%,\n" +
                        "    #f6f6f6 33.33%,\n" +
                        "    #f6f6f6 100%\n" +
                        "  )",
                }}
            >
                &nbsp;
            </div>
        </div>
    )
}

export default ContentContainer
